import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { isUndefined, sortBy } from 'lodash';
import { finalize, map } from 'rxjs/operators';
import { UiModule } from '../../../components/ui.module';
import { tryGetErrorMessage } from '../../../error.utils';
import { customSearchForIdAndName } from '../../../search.utils';
import { LocationService } from '../../../service/location.service';
import { LocationResponse, LocationTiny } from '../../../types';

@Component({
  selector: 'app-page-select-location',
  templateUrl: './page-select-location.component.html',
  styleUrl: './page-select-location.component.scss',
  standalone: true,
  imports: [UiModule, NgSelectModule, FormsModule],
})
export class PageSelectLocationComponent {
  private locationService = inject(LocationService);

  public selected = model<LocationTiny[] | LocationTiny | null>(null);

  public multi = input<boolean>(false);
  public label = input<string>('Select Location');
  public placeholder = input<string>('Select location...');
  public autoSelectDefaultLocation = input<boolean>(false);
  public autoSelectLocationIds = input<number[]>(undefined);
  public showBranchName = input<boolean>(true);
  public disabled = input<boolean>(false);
  public defaultEmpty = input<boolean>(false);
  public branchId = input<number | undefined>(undefined);
  public overrideLocations = input<LocationTiny[]>([]);
  public forceShowOverrideLocationsEvenIfEmpty = input<boolean>(false);
  public locationFilterFn = input<((location: LocationResponse) => boolean) | undefined>(() => true);

  public defaultSelected = output<LocationTiny>();

  public isLoading = signal<boolean>(false);
  public error = signal<string | undefined>(undefined);
  public locations = signal<LocationTiny[]>([]);
  public customSearchFn = customSearchForIdAndName;

  public dropdownLocations = computed(() => {
    if (this.forceShowOverrideLocationsEvenIfEmpty()) return this.overrideLocations();
    return this.overrideLocations().length > 0 ? this.overrideLocations() : this.locations();
  });
  public visibleLocations = computed(() => sortBy(this.dropdownLocations().filter(this.locationFilterFn()), (b) => b.name.trim()));

  constructor() {
    effect(() => {
      if (this.defaultEmpty()) return;

      const newBranchId = this.branchId();
      if (isUndefined(newBranchId)) {
        this.getAllLocations();
        return;
      }

      this.getLocations(newBranchId);
    });
  }

  public reset() {
    this.locations.set([]);
    this.selected.set(null);
  }

  private getAllLocations() {
    if (this.forceShowOverrideLocationsEvenIfEmpty() || this.overrideLocations().length > 0) return;

    this.isLoading.set(true);
    this.locationService
      .getAllLocations()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (locations) => this.setLocations(locations.items),
        (err) => this.error.set(tryGetErrorMessage(err)),
      );
  }

  public getLocations(branchId: number) {
    this.isLoading.set(true);
    this.setLocations([]);

    this.locationService
      .getAllLocations({ branch_ids: [branchId] })
      .pipe(
        map((res) =>
          res.items.map((location) => ({
            id: location.id,
            name: location.name,
            timezone: location.timezone,
            location_group_id: location.location_group_id,
          })),
        ),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe(
        (locations) => this.setLocations(locations),
        (err) => this.error.set(tryGetErrorMessage(err)),
      );
  }

  public setLocations(locations: LocationTiny[]) {
    this.locations.set(locations);

    if (this.autoSelectDefaultLocation()) {
      this.selectDefaultLocation();
    }

    const loadIds = this.autoSelectLocationIds();
    if (!isUndefined(loadIds) && loadIds.length > 0) {
      this.selectLocations(loadIds.map((i) => +i));
    }
  }

  public selectDefaultLocation() {
    if (this.locations().length === 0) return;

    this.selectLocations([this.visibleLocations()[0]?.id]);

    this.defaultSelected.emit(this.visibleLocations()[0]);
  }

  public selectLocations(locationIds: number[]) {
    const foundLocations = this.locations().filter((b) => locationIds.includes(b.id));

    this.selected.set(this.multi() ? foundLocations : foundLocations[0]);
  }
}
